import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Badge,
  Card,
  EmailInput,
  Modal,
  PageStatus,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import {
  BillingProviderEnum,
  type CommandAdminApi,
  commandStoreApi,
} from 'corso-types';
import { type FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';
import { formatter } from 'corso-web-lib';
import { StorePage } from '../StorePage';
import { CommissionArrangementsCard } from './CommissionArrangementCard';
import { InvoiceDefinitionsCard } from './InvoiceDefinitionCard';

type Invoice = CommandAdminApi.ResponseBody<
  '/store/:storeId/invoice',
  'get'
>[number];
type InvoiceSection = Invoice['sections'][number];
type InvoiceAdjustment = Invoice['adjustments'][number];
type InvoiceCommissionCalculation = Invoice['commissionCalculations'][number];
type Store = CommandAdminApi.ResponseBody<'/store/:storeId', 'get'>;

type BillingProvider = Store['billingProviders'][number];

const { idColumn, createdOnColumn } = sharedTableDefinitions;

const handleCurrency = (value: number) => formatter.currency(value, 'USD');
const handleDate = (value: string | null) =>
  value ? formatter.date(value) : '—';

const mapBp = (val: string | null) => {
  if (val === BillingProviderEnum.shopifySubscription) return 'Shopify';
  if (val === BillingProviderEnum.stripe) return 'Stripe';
  return '-';
};

const invoiceColumns: ColumnDef<Invoice>[] = [
  idColumn<Invoice>({ meta: { align: 'left' } }),
  {
    accessorKey: 'totalAmount',
    header: 'Total',
    enableSorting: true,
    meta: { align: 'right' },
    cell: ({ row }) => handleCurrency(row.original.totalAmount),
  },
  {
    accessorKey: 'fromDate',
    header: 'From',
    cell: ({ row }) => handleDate(row.original.fromDate),
  },
  {
    accessorKey: 'toDate',
    header: 'To',
    cell: ({ row }) => handleDate(row.original.toDate),
  },

  {
    accessorKey: 'protectedOrderCount',
    header: 'Protected Orders',
    enableSorting: true,
    meta: { align: 'right' },
  },
  {
    accessorKey: 'reorderCount',
    header: 'Reorders',
    enableSorting: true,
    meta: { align: 'right' },
  },
  {
    accessorKey: 'refundCount',
    header: 'Refunds',
    enableSorting: true,
    meta: { align: 'right' },
  },
  {
    accessorKey: 'billingProvider',
    header: 'Paid On',
    meta: { align: 'right' },
    cell: ({ row }) => mapBp(row.original.billingProvider),
  },
  createdOnColumn<Invoice>(),
];

const sectionColumns: ColumnDef<InvoiceSection>[] = [
  idColumn<InvoiceSection>({ meta: { align: 'left' } }),
  { accessorKey: 'name', header: 'Name' },
  {
    accessorKey: 'amount',
    header: 'Amount',
    enableSorting: true,
    meta: { align: 'right' },
    cell: ({ row }) => handleCurrency(row.original.amount),
  },
  {
    accessorKey: 'supportingDocumentPath',
    header: 'Supporting Document',
    cell: ({ row }) =>
      row.original.supportingDocumentPath ? (
        <a
          href={row.original.supportingDocumentPath}
          target="_blank"
          rel="noreferrer"
          className="text-corso-blue-600 hover:underline"
        >
          View
        </a>
      ) : (
        '—'
      ),
  },
];

const adjustmentColumns: ColumnDef<InvoiceAdjustment>[] = [
  idColumn<InvoiceAdjustment>({ meta: { align: 'left' } }),
  {
    accessorKey: 'adjustmentType',
    header: 'Type',
    cell: ({ row }) => row.original.adjustmentType ?? '—',
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    enableSorting: true,
    meta: { align: 'right' },
    cell: ({ row }) => handleCurrency(row.original.amount),
  },
  {
    accessorKey: 'notes',
    header: 'Notes',
    cell: ({ row }) => row.original.notes ?? '—',
  },
  {
    accessorKey: 'internalNotes',
    header: 'Internal Notes',
    cell: ({ row }) => row.original.internalNotes ?? '—',
  },
];

const commissionColumns: ColumnDef<InvoiceCommissionCalculation>[] = [
  idColumn<InvoiceCommissionCalculation>({ meta: { align: 'left' } }),
  { accessorKey: 'commissionPayeeName', header: 'Payee' },
  {
    accessorKey: 'amount',
    header: 'Amount',
    enableSorting: true,
    meta: { align: 'right' },
    cell: ({ row }) => handleCurrency(row.original.amount),
  },
  {
    accessorKey: 'projectedAmount',
    header: 'Projected',
    enableSorting: true,
    meta: { align: 'right' },
    cell: ({ row }) => handleCurrency(row.original.projectedAmount),
  },
  {
    accessorKey: 'wasDeductedFromInvoice',
    header: 'Deducted',
    cell: ({ row }) => (row.original.wasDeductedFromInvoice ? 'Yes' : 'No'),
  },
];

function StoreInvoiceCard() {
  const storeId = useStoreId();
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );

  const query = useQuery({
    queryKey: ['store', storeId, 'invoices'],
    queryFn: () => api.store(storeId).listInvoices(),
  });

  return (
    <Card>
      <Card.Heading>Invoices</Card.Heading>
      {match(query)
        .with({ isPending: true }, () => (
          <PageStatus.Loading title="Loading invoices" />
        ))
        .with({ isError: true }, () => (
          <PageStatus.Error
            title="Failed to load invoices"
            error={query.error}
          />
        ))
        .with({ isSuccess: true, data: P.select() }, (invoices) => {
          if (invoices.length === 0) {
            return (
              <DataTableStatus
                icon="receipt"
                title="No invoices found"
                details="Invoices will appear here once the store has been billed."
              />
            );
          }

          const selectedInvoice = invoices.find(
            (invoice) => invoice.id === selectedInvoiceId,
          );

          return (
            <>
              <DataTable
                pageSize={10}
                data={invoices}
                columns={invoiceColumns}
                defaultSort={[{ id: 'createdOn', desc: true }]}
                onRowClick={(row) =>
                  setSelectedInvoiceId((current) =>
                    current === row.id ? null : row.id,
                  )
                }
              />

              {selectedInvoice && (
                <Modal
                  title={`Invoice ${selectedInvoice.id}`}
                  show={!!selectedInvoice}
                  onClose={() => setSelectedInvoiceId(null)}
                  size="xl"
                >
                  <Card>
                    <Card.Heading>Sections</Card.Heading>
                    {selectedInvoice.sections.length > 0 ? (
                      <DataTable
                        data={selectedInvoice.sections}
                        columns={sectionColumns}
                        pageSize={false}
                      />
                    ) : (
                      <DataTableStatus
                        icon="receipt"
                        title="No sections"
                        details="This invoice has no sections."
                      />
                    )}
                  </Card>

                  <Card>
                    <Card.Heading>Adjustments</Card.Heading>
                    {selectedInvoice.adjustments.length > 0 ? (
                      <DataTable
                        data={selectedInvoice.adjustments}
                        columns={adjustmentColumns}
                        pageSize={false}
                      />
                    ) : (
                      <DataTableStatus
                        icon="receipt"
                        title="No adjustments"
                        details="This invoice has no adjustments."
                      />
                    )}
                  </Card>

                  <Card>
                    <Card.Heading>Commission Calculations</Card.Heading>
                    {selectedInvoice.commissionCalculations.length > 0 ? (
                      <DataTable
                        data={selectedInvoice.commissionCalculations}
                        columns={commissionColumns}
                        pageSize={false}
                      />
                    ) : (
                      <DataTableStatus
                        icon="receipt"
                        title="No commission calculations"
                        details="This invoice has no commission calculations."
                      />
                    )}
                  </Card>
                </Modal>
              )}
            </>
          );
        })
        .exhaustive()}
    </Card>
  );
}

const billingProviderColumns: ColumnDef<BillingProvider>[] = [
  { accessorKey: 'appType', header: 'App', enableSorting: true },
  {
    accessorKey: 'provider',
    header: 'Provider',
    cell: ({ row }) => (
      <Badge variant="info">{mapBp(row.original.provider)}</Badge>
    ),
  },
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) =>
      row.original.provider === BillingProviderEnum.stripe
        ? row.original.name
        : '—',
  },
  {
    id: 'email',
    header: 'Email',
    cell: ({ row }) =>
      row.original.provider === BillingProviderEnum.stripe
        ? row.original.email
        : '—',
  },
  {
    id: 'stripeCustomerId',
    header: 'Stripe Customer ID',
    cell: ({ row }) =>
      row.original.provider === BillingProviderEnum.stripe
        ? row.original.stripeCustomerId
        : '—',
  },
];

const billingProviderSchema =
  commandStoreApi['/store/:storeId/billing-provider'].put.request.body;

type BillingProviderUpdate = CommandAdminApi.RequestBody<
  '/store/:storeId/billing-provider',
  'put'
>;

const providerOptions = [
  { value: 'Stripe', label: 'Stripe' },
  { value: 'Shopify_Subscription', label: 'Shopify' },
] satisfies { value: BillingProviderUpdate['provider']; label: string }[];

function EditBillingProviderModal({
  billingProviders,
  onClose,
}: {
  billingProviders: BillingProvider[];
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const existingStripe = billingProviders.find(
    (bp) => bp.provider === BillingProviderEnum.stripe,
  );

  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(billingProviderSchema),
    defaultValues: existingStripe
      ? {
          provider: 'Stripe' as const,
          name: existingStripe.name,
          email: existingStripe.email,
        }
      : { provider: 'Shopify_Subscription' as const },
  });

  const { mutate: updateBillingProvider, isPending } = useMutation({
    mutationFn: api.store(storeId).updateBillingProvider,
    onError: () => {
      toast.show('Failed to update billing provider');
    },
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Updated billing provider');
    },
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => updateBillingProvider(body))(event).catch(
      console.error,
    );
  };

  return (
    <Modal show onClose={onClose} title="Edit Billing Provider">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="provider"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Provider"
              required
              options={providerOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        {watch('provider') === BillingProviderEnum.stripe && (
          <>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <TextInput
                  id="name"
                  label="Name"
                  details="The name of the user to create in Stripe."
                  required
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <EmailInput
                  id="email"
                  label="Email"
                  details="The email to use for Stripe billing."
                  required
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
          </>
        )}
        <div className="flex justify-end">
          <Action variant="primary" type="submit" loading={isPending}>
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function BillingProviderCard() {
  const { billingProviders } = useStoreContext();
  const [showEdit, setShowEdit] = useState(false);

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Billing Provider</Card.Heading>
        <Action
          variant="ghost"
          icon="edit"
          accessibilityLabel="Edit Billing Provider"
          onClick={() => setShowEdit(true)}
        />
      </Card.Line>

      {billingProviders.length > 0 ? (
        <DataTable
          data={billingProviders}
          columns={billingProviderColumns}
          pageSize={false}
        />
      ) : (
        <DataTableStatus
          icon="receipt"
          title="No billing provider"
          details="Set a billing provider to start billing this store."
        />
      )}

      {showEdit && (
        <EditBillingProviderModal
          billingProviders={billingProviders}
          onClose={() => setShowEdit(false)}
        />
      )}
    </Card>
  );
}

export default function StoreBillingPage() {
  return (
    <StorePage title="Billing">
      <div className="flex flex-col gap-4">
        <InvoiceDefinitionsCard />
        <BillingProviderCard />
        <StoreInvoiceCard />

        <CommissionArrangementsCard />
      </div>
    </StorePage>
  );
}
